import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';
import type { SiteSocialLinks } from '../../shared/models/marketing.models';
import { PagesService } from '../../shared/services/pages.service';

@Component({
  selector: 'app-marketing-footer',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <footer class="cg-mkt-footer">
      <div class="cg-mkt-footer__inner">
        <div>
          <div class="cg-mkt-logo cg-mkt-logo--footer mb-2">
            <span class="cg-mkt-logo__img-frame">
              <img
                class="cg-mkt-logo__img"
                width="176"
                height="48"
                src="/assets/images/logo.webp"
                alt=""
              />
            </span>
            <span class="cg-mkt-logo__wordmark">Careergrad Global</span>
          </div>
          <p class="cg-mkt-footer__tagline">Trusted study-abroad guidance for ambitious students.</p>
          @let links = socialLinks();
          @if (links?.instagram || links?.facebook || links?.youtube) {
            <div class="cg-mkt-footer__social" aria-label="Social links">
              @if (links?.instagram) {
                <a class="cg-mkt-footer__social-link" [href]="links!.instagram" target="_blank" rel="noopener">
                  <span class="sr-only">Instagram</span>
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="cg-mkt-footer__social-icon">
                    <path
                      fill="currentColor"
                      d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9A5.5 5.5 0 0 1 16.5 22h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2Zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9ZM12 7a5 5 0 1 1 0 10a5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6a3 3 0 0 0 0-6Zm5.75-2.25a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Z"
                    />
                  </svg>
                </a>
              }
              @if (links?.facebook) {
                <a class="cg-mkt-footer__social-link" [href]="links!.facebook" target="_blank" rel="noopener">
                  <span class="sr-only">Facebook</span>
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="cg-mkt-footer__social-icon">
                    <path
                      fill="currentColor"
                      d="M13.5 22v-8h2.8l.5-3H13.5V9.2c0-.9.3-1.5 1.6-1.5h2V5.1c-.3 0-1.4-.1-2.7-.1c-2.7 0-4.5 1.6-4.5 4.7V11H7.2v3H10v8h3.5Z"
                    />
                  </svg>
                </a>
              }
              @if (links?.youtube) {
                <a class="cg-mkt-footer__social-link" [href]="links!.youtube" target="_blank" rel="noopener">
                  <span class="sr-only">YouTube</span>
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="cg-mkt-footer__social-icon">
                    <path
                      fill="currentColor"
                      d="M21.6 7.2a3 3 0 0 0-2.1-2.1C17.7 4.6 12 4.6 12 4.6s-5.7 0-7.5.5a3 3 0 0 0-2.1 2.1A31.2 31.2 0 0 0 2 12a31.2 31.2 0 0 0 .4 4.8a3 3 0 0 0 2.1 2.1c1.8.5 7.5.5 7.5.5s5.7 0 7.5-.5a3 3 0 0 0 2.1-2.1A31.2 31.2 0 0 0 22 12a31.2 31.2 0 0 0-.4-4.8ZM10 15.4V8.6L16.2 12L10 15.4Z"
                    />
                  </svg>
                </a>
              }
            </div>
          }
        </div>
        <div>
          <strong class="block mb-2">Explore</strong>
          <a routerLink="/" class="block py-1">Home</a>
          <a href="#destinations" class="block py-1">Destinations</a>
          <a routerLink="/auth/login" class="block py-1">Admin</a>
        </div>
        <div>
          <strong class="block mb-2">Legal</strong>
          <span class="opacity-80">&copy; 2026 Careergrad Global (A Unit of of SkillSpring Ventures LLP)</span>
        </div>
      </div>
    </footer>
  `,
})
export class MarketingFooterComponent {
  private readonly pages = inject(PagesService);

  readonly socialLinks = toSignal(
    this.pages.getSiteSocialLinks().pipe(catchError(() => of(undefined))),
    { initialValue: undefined as SiteSocialLinks | undefined },
  );
}
